import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
  AppBar,
  Button,
  ClassNameMap,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Toolbar,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import SearchIcon from '@mui/icons-material/Search';
import queryString from 'query-string';
import toast from 'react-hot-toast';

import {
  BranchPayoutOrder,
  AdminBranchPayoutReversal,
  AdminBranchPayoutService,
} from 'us-web-services';
import BranchPayoutOrderCard from './BranchPayoutOrderCard';
import useAuthentication from '../../store/useAuthentication';

const styles = theme => ({
  paper: {
    maxWidth: 936,
    margin: 'auto',
    overflow: 'hidden',
  },
  searchBar: {
    padding: '12px 0 8px 0',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  searchInput: {
    fontSize: theme.typography.fontSize,
  },
  block: {
    display: 'block',
  },
  contentWrapper: {
    margin: '20px 16px',
  },
});

const REVERSAL_REASONS = [
  { value: 'DUPLICATE_PAYMENT', label: 'Duplicate Payment' },
  { value: 'INCORRECT_AMOUNT', label: 'Incorrect Amount' },
  { value: 'PAYMENT_TO_WRONG_ACCOUNT', label: 'Payment to Wrong Account' },
  { value: 'OTHER', label: 'Other' },
];

function OrderLookup(props: RouteComponentProps & { classes: ClassNameMap }) {
  const authenticationState = useAuthentication()[0];
  const authenticatedUser = authenticationState.authentication.user;
  const { classes, location } = props;
  const searchParams = queryString.parse(location.search);
  const [order, setOrder] = useState<BranchPayoutOrder>(undefined);
  const [orderId, setOrderId] = useState<number>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isReversingPayout, setIsReversingPayout] = useState(false);
  const [reversalAmount, setReversalAmount] = useState<number>();
  const [reversalReason, setReversalReason] = useState<string>('');

  const getSearchId = () => searchParams?.id ? Number.parseInt(searchParams.id.toString(), 10) : null;

  const isPaymentAdmin = authenticatedUser.roles.filter(role => role.code === 'PAYMENT_ADMIN').length > 0;
  const isSuperUser = authenticatedUser.roles.filter(role => role.code === 'SUPER_USER').length > 0;
  const hasRequiredPermissions = isPaymentAdmin && isSuperUser;

  useEffect(() => {
    const currentSearchId = getSearchId();

    if (currentSearchId && !orderId) {
      const getOrder = async () => {
        const userId = authenticationState.authentication.user.id;
        const orderResponse = await AdminBranchPayoutService.getByFilter(
          `?orderId=${currentSearchId}&userId=${userId}`,
        );
        const orderData = orderResponse.data?.data?.pop();

        setOrder(orderData);
      };

      setOrderId(currentSearchId);
      getOrder();
    }
  }, [authenticationState.authentication, searchParams.id]);

  const getOrderOnEnter = async id => {
    try {
      const newOrderId = Number.parseInt(id, 10);
      const userId = authenticationState.authentication.user.id;
      const orderResponse = await AdminBranchPayoutService.getByFilter(
        `?orderId=${newOrderId}&userId=${userId}`,
      );
      const orderData = orderResponse.data?.data?.pop();

      setOrder(orderData);
    } catch {
      toast.error(`Order ID ${id} not found`);
    }
  };

  const closeConfirmDialog = () => {
    setShowConfirm(false);
    setReversalAmount(undefined);
    setReversalReason('');
  };

  const confirmReversePayout = async () => {
    setReversalAmount(order?.order?.totalOut || order?.order?.total || 0);
    setShowConfirm(true);
  };

  const doReversePayout = async () => {
    try {
      setIsReversingPayout(true);

      if (!reversalAmount || !reversalReason) {
        toast.error('Amount and reason are required for Branch reversals');
        setIsReversingPayout(false);

        return;
      }

      const branchReversal: AdminBranchPayoutReversal = {
        orderId: order.order.id,
        type: 'DEPOSIT',
        amount: reversalAmount,
        requestReason: reversalReason,
      };

      await AdminBranchPayoutService.reversePayout(branchReversal);
      toast.success('Branch payout reversed successfully.');

      closeConfirmDialog();
      setIsReversingPayout(false);
    } catch {
      closeConfirmDialog();
      setIsReversingPayout(false);
      toast.error('There was an error reversing the Branch payout.');
    }
  };

  const searchKeyPress = event => {
    if (event.key === 'Enter') {
      getOrderOnEnter(orderId);
    }
  };

  const updateOrderId = event => {
    setOrderId(event.target.value);
  };

  const updateReversalAmount = event => {
    const maxAmount = order?.order?.totalOut || order?.order?.total || 1000;

    if (event.target.value > maxAmount) {
      toast.error(`Amount cannot exceed $${maxAmount}`);
    } else {
      setReversalAmount(event.target.value);
    }
  };

  const updateReversalReason = event => {
    setReversalReason(event.target.value);
  };

  return (
    <>
      <Dialog open={showConfirm} onClose={closeConfirmDialog}>
        <DialogTitle>Confirm Branch Payout Reversal</DialogTitle>
        <DialogContent style={{ minWidth: 400 }}>
          <Typography gutterBottom>
            Reverse payout for <b>Order ID {order?.order?.id}</b>
          </Typography>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Reversal Amount'
                type='number'
                value={reversalAmount}
                onChange={updateReversalAmount}
                required
                helperText={`Maximum: $${order?.order?.totalOut || order?.order?.total || 0}`}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Reason for Reversal</InputLabel>
                <Select
                  value={reversalReason}
                  onChange={updateReversalReason}
                  label='Reason for Reversal'
                >
                  {REVERSAL_REASONS.map((reason) => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography variant='body2' color='textSecondary' style={{ marginTop: 16 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color='secondary'>
            Cancel
          </Button>
          <Button
            onClick={doReversePayout}
            disabled={isReversingPayout || !reversalAmount || !reversalReason}
            color='primary'
            autoFocus
          >
            Reverse Payout
          </Button>
        </DialogActions>
      </Dialog>
      { hasRequiredPermissions ?
        <Paper className={classes.paper}>
          <AppBar
            className={classes.searchBar}
            position='static'
            color='default'
            elevation={0}
          >
            <Toolbar>
              <Grid container spacing={2} alignItems='center'>
                <SearchIcon className={classes.block} color='inherit' />
                <Grid item xs>
                  <TextField
                    fullWidth
                    required
                    id='orderIdSearch'
                    label='Search by Order ID'
                    name='orderIdSearch'
                    onChange={updateOrderId}
                    onKeyPress={searchKeyPress}
                    type='number'
                    value={orderId}
                  />
                </Grid>
              </Grid>
            </Toolbar>
          </AppBar>
          <div className={classes.contentWrapper}>
            {order?.id ? (
              <BranchPayoutOrderCard order={order} reversePayout={confirmReversePayout} />
            ) : (
              <Typography color='textPrimary' align='center'>
                { orderId ? 'Order not found' : 'Search by order_id above. Only supports Branch orders.' }
              </Typography>
            )}
          </div>
        </Paper>
        :
        <Typography variant='h6' gutterBottom align='center' style={{ marginTop: 40 }}>
          You do not have permission to use this tool. Requires both Payment Admin and Super User roles.
        </Typography>
      }
    </>
  );
}

export default withStyles(OrderLookup, styles);
